import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { DownloadIcon, FilterIcon, ChevronRightIcon, InfoIcon } from 'lucide-react';
const CoverageOverview = () => {
  const [timeframe, setTimeframe] = useState('30days');
  const [productFilter, setProductFilter] = useState('Freedom PNS System');
  const [activeSegment, setActiveSegment] = useState(null);
  // Sample data for overall coverage status
  const overallCoverageData = [{
    name: 'Positive Coverage',
    value: 38.2,
    lives: '72.5M',
    color: '#22c55e'
  }, {
    name: 'Negative Coverage',
    value: 48.6,
    lives: '92.3M',
    color: '#ef4444'
  }, {
    name: 'No Policy',
    value: 13.2,
    lives: '25.1M',
    color: '#eab308'
  }];
  // Sample data for payer categories
  const payerCategoryData = [{
    name: 'National',
    positive: 35,
    negative: 55,
    noPolicy: 10,
    totalLives: '95.6M'
  }, {
    name: 'Regional',
    positive: 42,
    negative: 43,
    noPolicy: 15,
    totalLives: '65.2M'
  }, {
    name: 'State',
    positive: 38,
    negative: 47,
    noPolicy: 15,
    totalLives: '29.1M'
  }];
  // Sample data for payer types
  const payerTypeData = [{
    name: 'Medicare',
    positive: 52,
    negative: 38,
    noPolicy: 10,
    totalLives: '62.4M'
  }, {
    name: 'Medicaid',
    positive: 33,
    negative: 57,
    noPolicy: 10,
    totalLives: '75.8M'
  }, {
    name: 'Commercial',
    positive: 36,
    negative: 49,
    noPolicy: 15,
    totalLives: '51.7M'
  }];
  // Sample data for BCBS entities
  const bcbsData = [{
    name: 'BCBS Entities',
    positive: 41,
    negative: 45,
    noPolicy: 14,
    totalLives: '105.3M'
  }];
  // Sample data for top payers
  const topPayersData = [{
    name: 'UnitedHealthcare',
    status: 'negative',
    lives: '45.8M',
    percentTotal: 24.1,
    lastUpdate: 'Mar 15, 2025'
  }, {
    name: 'Anthem',
    status: 'negative',
    lives: '41.2M',
    percentTotal: 21.7,
    lastUpdate: 'Feb 22, 2025'
  }, {
    name: 'Aetna',
    status: 'negative',
    lives: '22.5M',
    percentTotal: 11.8,
    lastUpdate: 'Apr 10, 2025'
  }, {
    name: 'Cigna',
    status: 'negative',
    lives: '20.1M',
    percentTotal: 10.6,
    lastUpdate: 'Jan 30, 2025'
  }, {
    name: 'Humana',
    status: 'positive',
    lives: '16.8M',
    percentTotal: 8.9,
    lastUpdate: 'Dec 05, 2024'
  }, {
    name: 'Kaiser Permanente',
    status: 'no-policy',
    lives: '12.4M',
    percentTotal: 6.5,
    lastUpdate: 'N/A'
  }];
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-300">
      <div className="mb-6" data-id="element-301">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-302">
          <div data-id="element-303">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-304">
              Coverage Overview
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-305">
              Comprehensive view of coverage status across all payer categories
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-306">
            <select className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={productFilter} onChange={e => setProductFilter(e.target.value)} data-id="element-307">
              <option data-id="element-308">Freedom® PNS System</option>
              <option data-id="element-309">All Products</option>
            </select>
            <div className="flex p-1 bg-white rounded-lg shadow" data-id="element-310">
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '30days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('30days')} data-id="element-311">
                30 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '90days' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('90days')} data-id="element-312">
                90 Days
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded ${timeframe === '12months' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setTimeframe('12months')} data-id="element-313">
                12 Months
              </button>
            </div>
            <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" data-id="element-314">
              <FilterIcon className="w-5 h-5" data-id="element-315" />
            </button>
          </div>
        </div>
      </div>

      {/* Overall Coverage Status */}
      <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-3" data-id="element-316">
        <div className="lg:col-span-1 bg-white rounded-lg shadow" data-id="element-317">
          <div className="p-4 border-b border-gray-200" data-id="element-318">
            <div className="flex items-center justify-between" data-id="element-319">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-320">
                Overall Coverage Status
              </h3>
              <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-321">
                <DownloadIcon className="w-5 h-5" data-id="element-322" />
              </button>
            </div>
          </div>
          <div className="p-4 flex flex-col items-center" data-id="element-323">
            <div className="h-80 w-full" data-id="element-324">
              <ResponsiveContainer width="100%" height="100%" data-id="element-325">
                <PieChart data-id="element-326">
                  <Pie data={overallCoverageData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-327">
                    {overallCoverageData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-328" />)}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}%`, props.payload.name]} data-id="element-329" />
                  <Legend formatter={(value, entry) => {
                  const item = overallCoverageData.find(d => d.name === value);
                  return `${value}: ${item?.lives} (${item?.value}%)`;
                }} data-id="element-330" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 w-full" data-id="element-331">
              <div className="grid grid-cols-3 gap-4" data-id="element-332">
                {overallCoverageData.map((item, index) => <button key={index} className={`text-center p-3 rounded-lg transition-all duration-200 ${activeSegment === index ? 'bg-gray-100 shadow-sm' : 'hover:bg-gray-50'}`} onClick={() => setActiveSegment(activeSegment === index ? null : index)} data-id="element-333">
                    <div className="flex items-center justify-center mb-1" data-id="element-334">
                      <div className="w-3 h-3 rounded-full mr-1.5" style={{
                    backgroundColor: item.color
                  }} data-id="element-335" />
                      <span className="text-sm font-medium text-gray-700 truncate" data-id="element-336">
                        {item.name}
                      </span>
                    </div>
                    <div className="text-lg font-bold text-gray-900" data-id="element-337">
                      {item.lives}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-338">
                      {item.value}% of total
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-lg shadow" data-id="element-339">
          <div className="p-4 border-b border-gray-200" data-id="element-340">
            <div className="flex items-center justify-between" data-id="element-341">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-342">
                Coverage by Payer Category
              </h3>
              <div className="flex items-center space-x-2" data-id="element-343">
                <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-344">
                  <option data-id="element-345">By % of Lives</option>
                  <option data-id="element-346">By Total Lives</option>
                </select>
                <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-347">
                  <DownloadIcon className="w-5 h-5" data-id="element-348" />
                </button>
              </div>
            </div>
          </div>
          <div className="p-4" data-id="element-349">
            <div className="h-80" data-id="element-350">
              <ResponsiveContainer width="100%" height="100%" data-id="element-351">
                <BarChart layout="vertical" data={[...payerCategoryData, ...payerTypeData, ...bcbsData]} margin={{
                top: 20,
                right: 30,
                left: 100,
                bottom: 5
              }} data-id="element-352">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} data-id="element-353" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={value => `${value}%`} data-id="element-354" />
                  <YAxis dataKey="name" type="category" width={100} data-id="element-355" />
                  <Tooltip formatter={value => `${value}%`} data-id="element-356" />
                  <Legend data-id="element-357" />
                  <Bar dataKey="positive" stackId="a" fill="#22c55e" name="Positive Coverage" data-id="element-358" />
                  <Bar dataKey="negative" stackId="a" fill="#ef4444" name="Negative Coverage" data-id="element-359" />
                  <Bar dataKey="noPolicy" stackId="a" fill="#eab308" name="No Policy" data-id="element-360" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Coverage Category Links */}
      <div className="mb-8" data-id="element-361">
        <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-362">
          Coverage by Category
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-363">
          <Link to="/coverage/national" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-364">
            <div className="flex items-center justify-between" data-id="element-365">
              <div data-id="element-366">
                <h3 className="text-base font-medium text-gray-900" data-id="element-367">
                  National Payers
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-368">95.6M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-369" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-370">
              <div className="h-full flex" data-id="element-371">
                <div className="h-full bg-green-500" style={{
                width: '35%'
              }} data-id="element-372"></div>
                <div className="h-full bg-red-500" style={{
                width: '55%'
              }} data-id="element-373"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '10%'
              }} data-id="element-374"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/regional" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-375">
            <div className="flex items-center justify-between" data-id="element-376">
              <div data-id="element-377">
                <h3 className="text-base font-medium text-gray-900" data-id="element-378">
                  Regional Payers
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-379">65.2M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-380" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-381">
              <div className="h-full flex" data-id="element-382">
                <div className="h-full bg-green-500" style={{
                width: '42%'
              }} data-id="element-383"></div>
                <div className="h-full bg-red-500" style={{
                width: '43%'
              }} data-id="element-384"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '15%'
              }} data-id="element-385"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/state" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-386">
            <div className="flex items-center justify-between" data-id="element-387">
              <div data-id="element-388">
                <h3 className="text-base font-medium text-gray-900" data-id="element-389">
                  State Payers
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-390">29.1M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-391" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-392">
              <div className="h-full flex" data-id="element-393">
                <div className="h-full bg-green-500" style={{
                width: '38%'
              }} data-id="element-394"></div>
                <div className="h-full bg-red-500" style={{
                width: '47%'
              }} data-id="element-395"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '15%'
              }} data-id="element-396"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/medicare" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-397">
            <div className="flex items-center justify-between" data-id="element-398">
              <div data-id="element-399">
                <h3 className="text-base font-medium text-gray-900" data-id="element-400">
                  Medicare
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-401">62.4M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-402" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-403">
              <div className="h-full flex" data-id="element-404">
                <div className="h-full bg-green-500" style={{
                width: '52%'
              }} data-id="element-405"></div>
                <div className="h-full bg-red-500" style={{
                width: '38%'
              }} data-id="element-406"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '10%'
              }} data-id="element-407"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/medicaid" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-408">
            <div className="flex items-center justify-between" data-id="element-409">
              <div data-id="element-410">
                <h3 className="text-base font-medium text-gray-900" data-id="element-411">
                  Medicaid
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-412">75.8M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-413" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-414">
              <div className="h-full flex" data-id="element-415">
                <div className="h-full bg-green-500" style={{
                width: '33%'
              }} data-id="element-416"></div>
                <div className="h-full bg-red-500" style={{
                width: '57%'
              }} data-id="element-417"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '10%'
              }} data-id="element-418"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/commercial" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-419">
            <div className="flex items-center justify-between" data-id="element-420">
              <div data-id="element-421">
                <h3 className="text-base font-medium text-gray-900" data-id="element-422">
                  Commercial
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-423">51.7M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-424" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-425">
              <div className="h-full flex" data-id="element-426">
                <div className="h-full bg-green-500" style={{
                width: '36%'
              }} data-id="element-427"></div>
                <div className="h-full bg-red-500" style={{
                width: '49%'
              }} data-id="element-428"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '15%'
              }} data-id="element-429"></div>
              </div>
            </div>
          </Link>
          <Link to="/coverage/bcbs" className="block p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow" data-id="element-430">
            <div className="flex items-center justify-between" data-id="element-431">
              <div data-id="element-432">
                <h3 className="text-base font-medium text-gray-900" data-id="element-433">
                  Blue Cross Blue Shield
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-434">105.3M lives</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400" data-id="element-435" />
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-436">
              <div className="h-full flex" data-id="element-437">
                <div className="h-full bg-green-500" style={{
                width: '41%'
              }} data-id="element-438"></div>
                <div className="h-full bg-red-500" style={{
                width: '45%'
              }} data-id="element-439"></div>
                <div className="h-full bg-yellow-500" style={{
                width: '14%'
              }} data-id="element-440"></div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Top Payers Table */}
      <div className="bg-white rounded-lg shadow mb-8" data-id="element-441">
        <div className="p-4 border-b border-gray-200" data-id="element-442">
          <div className="flex items-center justify-between" data-id="element-443">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-444">
              Top Payers by Lives Covered
            </h3>
            <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-445">
              <DownloadIcon className="w-5 h-5" data-id="element-446" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-447">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-448">
            <thead className="bg-gray-50" data-id="element-449">
              <tr data-id="element-450">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-451">
                  Payer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-452">
                  Coverage Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-453">
                  Covered Lives
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-454">
                  % of Total
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-455">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-456">
              {topPayersData.map((payer, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-457">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-458">
                    <div className="text-sm font-medium text-blue-600" data-id="element-459">
                      {payer.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-460">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${payer.status === 'positive' ? 'bg-green-100 text-green-800' : payer.status === 'negative' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-461">
                      {payer.status === 'positive' ? 'Positive' : payer.status === 'negative' ? 'Negative' : 'No Policy'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-462">
                    <div className="text-sm text-gray-900" data-id="element-463">{payer.lives}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-464">
                    <div className="text-sm text-gray-900" data-id="element-465">
                      {payer.percentTotal}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-466">
                    <div className="text-sm text-gray-500" data-id="element-467">
                      {payer.lastUpdate}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-200" data-id="element-468">
          <div className="flex justify-between items-center" data-id="element-469">
            <div className="flex items-center text-sm text-gray-500" data-id="element-470">
              <InfoIcon className="w-4 h-4 mr-1" data-id="element-471" />
              <span data-id="element-472">Showing top 6 payers by covered lives</span>
            </div>
            <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-473">
              View All Payers →
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default CoverageOverview;